import { motion, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { FILMS } from "../lib/films";
import useInView from "../hooks/useInView";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import { VideoModal } from "./VideoModal";

const TABS = [
  { id: "films", label: "Films" },
  { id: "commercials", label: "Commercials" },
];

const COMMERCIALS = [
  { id: "eDeRSrOUsjE", label: "Commercial · 01" },
  { id: "0dpZaBU59mc", label: "Commercial · 02 — Maddie" },
  { id: "5eiOhUn7GlI", label: "Commercial · 03" },
  { id: "cEzt0ZL_JuM", label: "Commercial · 04" },
  { id: "gWfrdpDSBZ4", label: "Commercial · 05" },
];

const Carousel = ({ children, testId, itemCount, subtitle = "Drag or use arrows" }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    const step = el.clientWidth * 0.82;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-6 gap-6">
        <div className="flex items-center gap-4 font-mono text-[10px] tracking-widest2 uppercase text-muted">
          <span className="hidden sm:inline">{subtitle}</span>
          <span className="text-sun">{itemCount} REELS</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:block w-40 h-px bg-ink/15 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-sun"
              style={{ width: `${Math.max(6, progress * 100)}%`, transition: "width 0.3s cubic-bezier(0.16,1,0.3,1)" }}
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              data-testid={`${testId}-prev`}
              onClick={() => scroll(-1)}
              className="w-11 h-11 rounded-full border border-ink/25 hover:border-sun hover:text-sun hover:bg-sun/5 text-ink inline-flex items-center justify-center hover-fade"
              aria-label="Previous"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              data-testid={`${testId}-next`}
              onClick={() => scroll(1)}
              className="w-11 h-11 rounded-full border border-ink/25 hover:border-sun hover:text-sun hover:bg-sun/5 text-ink inline-flex items-center justify-center hover-fade"
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
      <div
        ref={ref}
        onScroll={onScroll}
        data-testid={testId}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 no-scrollbar"
        style={{ scrollPaddingLeft: "1.5rem" }}
      >
        {children}
      </div>
    </div>
  );
};

const PosterCard = ({ film, index, onPlay }) => {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <motion.article
      ref={ref}
      data-testid={`film-${film.id}`}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
      className="group relative snap-start shrink-0 w-[86vw] md:w-[72vw] lg:w-[62vw] overflow-hidden border border-white/10 hover:border-sun/60 hover-fade"
    >
      <button
        type="button"
        onClick={onPlay}
        data-testid={`film-cta-${film.id}`}
        className="block w-full text-left"
      >
        <div className="relative aspect-[21/12] md:aspect-[21/9] overflow-hidden bg-bg-2">
          <img
            src={film.image}
            alt={film.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] hover-fade"
            style={{
              ...film.imageStyle,
              filter: "brightness(0.5) contrast(1.1) saturate(0.95)",
            }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-bg/90 via-bg/45 to-transparent" />
          <div className="absolute inset-0 grain" />

          <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-10">
            <div className="flex items-center justify-between font-mono text-[10px] tracking-widest2 uppercase text-ink/75">
              <span>Film N° {String(index + 1).padStart(2, "0")}</span>
              <span>{film.year} · {film.runtime}</span>
            </div>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="max-w-[26ch]">
                <h3
                  data-testid={`film-title-${film.id}`}
                  className="font-display italic text-ink leading-[0.88] text-[13vw] md:text-[6vw] lg:text-[4.6vw] tracking-tight group-hover:text-sun hover-fade"
                >
                  {film.title}
                </h3>
                <p
                  data-testid={`film-logline-${film.id}`}
                  className="mt-4 md:mt-6 font-display italic text-lg md:text-xl text-ink/80 leading-snug max-w-xl"
                >
                  {film.logline}
                </p>
                {film.festivals?.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {film.festivals.map((f) => (
                      <span
                        key={f}
                        className="inline-flex items-center gap-2 px-3 py-1 border border-sun/50 rounded-full font-mono text-[10px] tracking-widest2 uppercase text-sun"
                      >
                        ✦ {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sun text-bg group-hover:scale-110 hover-fade">
                  <Play size={18} fill="currentColor" />
                </span>
                <span className="font-mono text-[11px] tracking-widest2 uppercase text-ink">
                  Watch trailer
                </span>
              </div>
            </div>
          </div>
        </div>
      </button>
    </motion.article>
  );
};

const CommercialCard = ({ commercial }) => (
  <div
    data-testid={`commercial-${commercial.id}`}
    className="snap-start shrink-0 w-[86vw] md:w-[52vw] lg:w-[42vw]"
  >
    <div className="relative aspect-video border border-white/10 overflow-hidden bg-bg-2">
      <iframe
        src={`https://www.youtube.com/embed/${commercial.id}?rel=0`}
        title={commercial.label}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full block bg-bg-2"
      />
    </div>
    <div className="mt-3 font-mono text-[10px] tracking-widest2 uppercase text-muted flex items-center justify-between">
      <span>{commercial.label}</span>
      <span className="text-sun">◉ LIVE</span>
    </div>
  </div>
);

export const Films = () => {
  const [active, setActive] = useState("films");
  const [playing, setPlaying] = useState(null);
  const [headRef, headIn] = useInView({ threshold: 0.15 });

  return (
    <section
      id="work"
      data-testid="films-section"
      className="relative bg-bg py-24 md:py-32 border-t border-white/[0.05]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-16 font-mono text-[11px] tracking-widest2 uppercase text-muted">
          <span className="w-10 h-px bg-sun" />
          <span className="text-sun">(03)</span>
          <span>Selected Work</span>
        </div>

        <motion.div
          ref={headRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headIn ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20 items-end"
        >
          <h2
            data-testid="films-heading"
            className="lg:col-span-8 font-display text-ink text-[14vw] md:text-[9vw] lg:text-[7vw] leading-[0.9] tracking-tight"
          >
            The <em className="italic text-sun">work.</em>
          </h2>
          <p className="lg:col-span-4 font-display italic text-xl md:text-2xl text-ink/80 leading-snug max-w-md">
            Films behind the camera. Commercials in the world. Toggle a reel to step inside.
          </p>
        </motion.div>

        {/* Tab strip */}
        <div
          data-testid="work-tabs"
          role="tablist"
          aria-label="Work reels"
          className="flex items-center gap-8 border-b border-white/10 mb-16 md:mb-20"
        >
          {TABS.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                data-testid={`tab-${t.id}`}
                onClick={() => setActive(t.id)}
                className={`relative py-5 font-mono text-[11px] tracking-widest2 uppercase hover-fade ${
                  isActive ? "text-ink" : "text-muted hover:text-ink"
                }`}
              >
                {t.label}
                {isActive && (
                  <motion.span
                    layoutId="tab-underline"
                    className="absolute left-0 right-0 -bottom-px h-[2px] bg-sun"
                    transition={{ type: "spring", stiffness: 320, damping: 32 }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            {active === "films" && (
              <Carousel testId="tab-content-films" itemCount={FILMS.length}>
                {FILMS.map((f, i) => (
                  <PosterCard
                    key={f.id}
                    film={f}
                    index={i}
                    onPlay={() => f.reel && setPlaying(f)}
                  />
                ))}
              </Carousel>
            )}

            {active === "commercials" && (
              <Carousel testId="tab-content-commercials" itemCount={COMMERCIALS.length}>
                {COMMERCIALS.map((c) => (
                  <CommercialCard key={c.id} commercial={c} />
                ))}
              </Carousel>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <VideoModal
        open={!!playing}
        url={playing?.reel}
        title={playing?.title}
        onClose={() => setPlaying(null)}
      />
    </section>
  );
};
